import { ReactElement, useCallback, useEffect, useState } from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { Form } from 'react-bootstrap';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import LineAwesome from '../LineAwesome/LineAwesome';
import { geocodeLocation } from '../../utils/locationUtils';
import { GeocoderResult } from '../../types/googleMaps';
import { Logger } from '../../core/logger/Logger';
import { regionStore } from '../../store/jotai/store';
import { coordsAtom } from '../../store/jotai/atoms/coords.atom';
import { geolocationService } from '../../services/geolocation-service';
import { useGoogleMapsReady } from '../../utils/hooks/useGoogleMapsReady';
import './LocationField.scss';

type LocationFieldProps = {
  className?: string;
  onHandleSelect?: () => void;
};

export const LocationField = ({
  className = '',
  onHandleSelect
}: LocationFieldProps): ReactElement => {
  const { t } = useTranslation();
  const coords = useAtomValue(coordsAtom, { store: regionStore });
  const formattedAddress = coords.geocodeAddress?.formatted_address;
  const [address, setAddress] = useState(formattedAddress ?? '');
  const [locating, setLocating] = useState(false);
  const [sessionToken, setSessionToken] = useState<any>(null);
  // The autocomplete mounts only once the Maps script is there. Mounted earlier,
  // it would wait for the one shared `window.initMaps` callback, which a field
  // mounted after it takes over, and stay disabled.
  const mapsReady = useGoogleMapsReady();

  const refreshSessionToken = useCallback((): void => {
    setSessionToken(new (window as any).google.maps.places.AutocompleteSessionToken());
  }, []);

  const locate = useCallback((): void => {
    setLocating(true);
    geolocationService.reset();

    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          geolocationService.updateLocation(
            position.coords.latitude,
            position.coords.longitude,
            true
          );
          geolocationService.resolveGeoAddress();

          geocodeLocation(position.coords.latitude, position.coords.longitude)
            .then((response: GeocoderResult) => {
              setLocating(false);
              setAddress(response.formatted_address);
            })
            .catch((error) => {
              Logger.error(error);
              setLocating(false);
            });
        },
        (error) => {
          Logger.error(error);
          setLocating(false);
        },
        { timeout: 15000, enableHighAccuracy: true, maximumAge: Infinity }
      );
    } catch (error) {
      Logger.error(error);
      setLocating(false);
    }
  }, []);

  // Without a location yet, the visitor's own is looked up once.
  useEffect(() => {
    if (!regionStore.get(coordsAtom).latitude) {
      locate();
    }
  }, [locate]);

  // A location picked anywhere (here, another location field, the locate button)
  // shows up in every field.
  useEffect(() => {
    if (formattedAddress) {
      setAddress(formattedAddress);
      setLocating(false);
    }
  }, [formattedAddress]);

  useEffect(() => {
    if (mapsReady) {
      refreshSessionToken();
    }
  }, [mapsReady, refreshSessionToken]);

  const handleSelect = (value: string): void => {
    geocodeByAddress(value)
      .then((results) => getLatLng(results[0]))
      .then((latLng) => {
        refreshSessionToken();
        geolocationService.updateLocation(latLng.lat, latLng.lng, value);
        geolocationService.resolveGeoAddress();
      })
      .catch((error) => Logger.error('Error', error));

    setAddress(value);
    onHandleSelect?.();
  };

  // Suggestions near the current location first.
  const searchOptions = {
    sessionToken,
    ...(coords.latitude && {
      location: { lat: () => coords.latitude, lng: () => coords.longitude },
      radius: 10000
    })
  } as any;
  const placeholder = t('locationField.placeholder');

  const locateButton = (
    <button type='button' className='locate-me' onClick={locate} disabled={locating}>
      <LineAwesome icon='location-arrow' /> <span>{t('locationField.detectLocation')}</span>{' '}
      {locating && '...'}
    </button>
  );

  return mapsReady ? (
    <PlacesAutocomplete
      value={locating ? '' : address}
      onChange={setAddress}
      onSelect={handleSelect}
      searchOptions={searchOptions}>
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <Form.Group className={`${className} location-field`}>
          <Form.Control
            {...getInputProps({ placeholder, className: 'location-search-input', size: 'lg' })}
            value={address}
            disabled={locating}
          />
          {locateButton}
          <div className='autocomplete-dropdown-container'>
            {loading && <div className='suggestion-item'>{t('loadingIndicator.loading')}</div>}
            {suggestions.map((suggestion) => (
              <div
                {...getSuggestionItemProps(suggestion, {
                  className: suggestion.active ? 'suggestion-item active' : 'suggestion-item'
                })}
                key={`suggestion-${suggestion.placeId}`}>
                <span>{suggestion.description}</span>
              </div>
            ))}
          </div>
        </Form.Group>
      )}
    </PlacesAutocomplete>
  ) : (
    <Form.Group className={`${className} location-field`}>
      <Form.Control
        placeholder={placeholder}
        className='location-search-input'
        size='lg'
        value={address}
        disabled
      />
      {locateButton}
    </Form.Group>
  );
};
